import { useCallback, useRef, useState } from 'react';

/**
 * Tells you when an element has first entered the viewport.
 *
 * Latching rather than tracking: once true it stays true and the observer is
 * dropped. Callers use this to decide whether to fetch something, and a
 * fetch already made must not be undone by scrolling away and back.
 *
 * The ref is a callback ref so the observer attaches the moment the node
 * mounts — an effect reading `ref.current` would miss a node that mounts and
 * intersects in the same frame, which is exactly the above-the-fold case.
 */
export function useInView<T extends Element>({ rootMargin = '200px' } = {}) {
  const [inView, setInView] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const ref = useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node) return;

      // No IntersectionObserver means no way to defer: show everything rather
      // than leave the grid permanently blank.
      if (typeof IntersectionObserver === 'undefined') {
        setInView(true);
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          setInView(true);
          observer.disconnect();
        },
        // A margin, so a thumbnail is in flight shortly before it is scrolled to
        // and lands without a visible gap.
        { rootMargin },
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [rootMargin],
  );

  return { ref, inView };
}
